import styled from "styled-components";
import { VoiceOfCustomerQuoteTag } from "../../../../../types/CommunityMediaConfig";

// Shared styled primitives for the brand editors (audience voice, format
// profile, visual style). Extracted from MediaStudioV2 so the editors can live
// in their own files without duplicating styles.

export const VocBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

export const VocChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const VocChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 4px 4px var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  line-height: 1.3;
`;

export const VocChipText = styled.span`
  display: inline-block;
  max-width: 22ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VocChipTag = styled.span<{ $tag: VoiceOfCustomerQuoteTag }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  font-size: 9px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-inverse);
  background: ${(p) =>
    p.$tag === "pain"
      ? "var(--color-danger)"
      : p.$tag === "desire"
      ? "var(--color-accent)"
      : p.$tag === "identity"
      ? "var(--color-info)"
      : p.$tag === "objection"
      ? "var(--color-warning)"
      : "var(--color-text-secondary)"};
`;

export const VocChipRemove = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  line-height: 1;
  font-size: 14px;
  padding: 0 2px;

  &:hover {
    color: var(--color-danger);
  }
`;

export const VocAddRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

export const VocAddInput = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 64px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

export const VocActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
`;

export const VocTagSelect = styled.select`
  appearance: none;
  height: 32px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;
`;

export const VocNotes = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 64px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

export const VocMeta = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

export const ResearchPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-accent);
`;

export const ResearchTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-text);
`;

export const ResearchPersona = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  line-height: var(--line-height-base);
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
`;

export const ResearchToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
`;

export const ResearchQuoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ResearchQuoteRow = styled.label<{ $picked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: ${(p) => (p.$picked ? "var(--color-surface)" : "transparent")};
  border: 1px solid
    ${(p) => (p.$picked ? "var(--color-border-strong)" : "transparent")};
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface);
  }
`;

export const ResearchQuoteCheckbox = styled.input.attrs({ type: "checkbox" })`
  margin-top: 2px;
  flex-shrink: 0;
`;

export const ResearchQuoteText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  line-height: 1.35;
  flex: 1;
  min-width: 0;
`;

export const ResearchQuoteMeta = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-tertiary);
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

export const FormatProfileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormatProfileRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  overflow: hidden;
`;

export const FormatProfileBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--color-accent-subtle);
  border-right: 2px solid var(--color-accent);
  z-index: 0;
  pointer-events: none;
`;

export const FormatProfileLabel = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
`;

export const FormatProfileName = styled.span`
  color: var(--color-text);
  text-transform: capitalize;
`;

export const FormatProfileWeight = styled.span`
  font-family: var(--font-mono);
  color: var(--color-accent-text);
`;

export const FormatProfileEvidence = styled.div`
  position: relative;
  z-index: 1;
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-secondary);
  line-height: 1.35;
`;

export const DiscoveryCard = styled.label<{ $picked: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border-radius: var(--radius-md);
  background: ${(p) => (p.$picked ? "var(--color-surface)" : "var(--color-surface-subtle)")};
  border: 1px solid
    ${(p) => (p.$picked ? "var(--color-accent)" : "var(--color-border-subtle)")};
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

export const DiscoveryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

export const DiscoveryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-size: 9px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const DiscoveryKey = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
  margin-left: auto;
`;

export const DiscoveryStructure = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

export const DiscoveryMeta = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-tertiary);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const SavedFormatsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border-subtle);
`;

export const SavedFormatsTitle = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

export const SavedFormatRow = styled.div<{ $failed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: var(--color-surface);
  border: 1px solid
    ${(p) => (p.$failed ? "var(--color-danger)" : "var(--color-border-subtle)")};
`;

export const SavedFormatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text);
`;

export const SavedFormatLabel = styled.span`
  font-weight: var(--font-weight-semibold);
`;

export const SavedFormatKey = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-tertiary);
`;

export const SavedFormatStructure = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

export const SavedFormatActions = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-top: 2px;
`;

export const DiscoverySectionTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ResearchSourcesNote = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-tertiary);
  word-break: break-all;
`;

export const VOC_TAGS: { id: VoiceOfCustomerQuoteTag; label: string }[] = [
  { id: "pain", label: "Pain" },
  { id: "desire", label: "Desire" },
  { id: "identity", label: "Identity" },
  { id: "objection", label: "Objection" },
  { id: "language", label: "Language" },
];
